/**
 * HomeScreen Store - Zustand Implementation
 *
 * Manages the registry of tiles displayed on the iOS-style home screen.
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { createLogger } from '../utils/logger';

const logger = createLogger('HomeScreenStore');

export interface HomeTile {
  id: string;
  label: string;
  icon: string;
  route: string;
  section?: 'main' | 'dock' | string;
  order?: number;
  visible?: () => boolean;
  badge?: () => string | number | null;
}

export interface HomeSection {
  id: string;
  title?: string;
  order: number;
  items: HomeTile[];
}

interface HomeScreenState {
  items: Map<string, HomeTile>;
  sections: Map<string, HomeSection>;
  registerTile: (tile: HomeTile) => () => void;
  unregisterTile: (tileId: string) => void;
  registerSection: (section: HomeSection) => void;
  getSections: () => HomeSection[];
  getTilesBySection: (sectionId: string) => HomeTile[];
  _initDefaultSections: () => void;
}

// Custom storage to handle Map serialization
const customStorage = {
  getItem: (name: string) => {
    const str = localStorage.getItem(name);
    if (!str) return null;

    try {
      const parsed = JSON.parse(str);
      return {
        state: {
          ...parsed.state,
          items: new Map(parsed.state.items || []),
          sections: new Map(
            (parsed.state.sections || []).map(([key, section]: [string, HomeSection]) => [
              key,
              { ...section }
            ])
          ),
        },
      };
    } catch (e) {
      logger.error('Failed to parse homescreen storage:', e);
      return null;
    }
  },
  setItem: (name: string, value: any) => {
    try {
      const serialized = {
        state: {
          ...value.state,
          items: Array.from(value.state.items.entries()),
          sections: Array.from(value.state.sections.entries()),
        },
      };
      localStorage.setItem(name, JSON.stringify(serialized));
    } catch (e) {
      logger.error('Failed to save homescreen storage:', e);
    }
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

export const useHomeScreenStore = create<HomeScreenState>()(
  devtools(
    persist(
      (set, get) => ({
        items: new Map(),
        sections: new Map(),

        _initDefaultSections: () => {
          const defaultSections: HomeSection[] = [
            { id: 'main', title: undefined, order: 0, items: [] },
            { id: 'dock', title: undefined, order: 1, items: [] },
          ];

          const newSections = new Map<string, HomeSection>();
          defaultSections.forEach(section => {
            newSections.set(section.id, section);
          });

          set({ sections: newSections });
        },

        registerTile: (tile: HomeTile) => {
          logger.debug(`Registering tile: ${tile.id}`);

          set((state) => {
            const newItems = new Map(state.items);
            const newSections = new Map(state.sections);

            newItems.set(tile.id, tile);

            newSections.forEach(section => {
              section.items = section.items.filter(existing => existing.id !== tile.id);
            });

            const sectionId = tile.section || 'main';
            const section = newSections.get(sectionId);

            if (section) {
              section.items.push(tile);
              section.items.sort((a, b) => (a.order || 0) - (b.order || 0));
            }

            return { items: newItems, sections: newSections };
          });

          return () => get().unregisterTile(tile.id);
        },

        unregisterTile: (tileId: string) => {
          set((state) => {
            const newItems = new Map(state.items);
            const newSections = new Map(state.sections);

            newItems.delete(tileId);
            newSections.forEach(section => {
              section.items = section.items.filter(item => item.id !== tileId);
            });

            return { items: newItems, sections: newSections };
          });
        },

        registerSection: (section: HomeSection) => {
          set((state) => {
            const newSections = new Map(state.sections);
            newSections.set(section.id, section);
            return { sections: newSections };
          });
        },

        getSections: () => {
          const state = get();
          return Array.from(state.sections.values())
            .sort((a, b) => a.order - b.order);
        },

        getTilesBySection: (sectionId: string) => {
          const state = get();
          const section = state.sections.get(sectionId);
          return section?.items || [];
        },
      }),
      {
        name: 'homescreen-storage',
        storage: customStorage,
        partialize: (state) => ({
          items: state.items,
          sections: state.sections,
        }),
      }
    ),
    { name: 'HomeScreenStore' }
  )
);

useHomeScreenStore.getState()._initDefaultSections();

export function useHomeSections() {
  return useHomeScreenStore((state) => state.getSections());
}

export function useHomeSection(sectionId: string) {
  return useHomeScreenStore((state) => state.getTilesBySection(sectionId));
}

export function useRegisterHomeTile() {
  return useHomeScreenStore((state) => state.registerTile);
}

export function registerHomeTile(tile: HomeTile) {
  return useHomeScreenStore.getState().registerTile(tile);
}
